import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { JsonScriptId } from '@/constants';
import { useGetStoredAddresses } from '@/hooks/useGetStoredAddresses';
import type { UserData } from '@/types';
import { getEmbeddedJson } from '@/utils/getEmbeddedJson';
import type { ProviderProps } from './types';

const UserContext = createContext<UserData | null>(null);

export const UserProvider = ({ children }: ProviderProps) => {
  const {
    deliveryAddresses = [],
    email,
    isAuthenticated,
    loyaltyCardId,
  }: UserData = getEmbeddedJson(JsonScriptId.User);

  const [storedAddresses, setStoredAddresses] = useState(deliveryAddresses);

  const updatedDeliveryAddresses = useGetStoredAddresses(isAuthenticated);

  useEffect(() => {
    if (
      updatedDeliveryAddresses.length > 0 &&
      JSON.stringify(deliveryAddresses) !==
        JSON.stringify(updatedDeliveryAddresses)
    ) {
      setStoredAddresses(updatedDeliveryAddresses);
    }
  }, [deliveryAddresses, updatedDeliveryAddresses]);

  const userData = useMemo(
    () => ({
      deliveryAddresses: storedAddresses,
      email,
      isAuthenticated,
      loyaltyCardId,
    }),
    [email, isAuthenticated, storedAddresses, loyaltyCardId],
  );

  return (
    <UserContext.Provider value={userData}>{children}</UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (context === undefined || context === null) {
    throw new Error('useUser must be used within an UserContext Provider');
  }

  return context;
};
